import { mountCreatorSetup } from './magnet-creator-setup.js';
import { mountShopifyFinder } from './magnet-shopify-finder.js';
import AuthController from '../services/AuthController.js';
import { guardedFetch } from '../core/navGeneration.js';

// The normal creator owns persistence, idempotency and navigation. This adapter
// supplies only a reviewed catalog proposal, using the existing finder editor.
export function mountCreatorWithShopify(root, options) {
  const ordinary = document.createElement('div'), commerce = document.createElement('div');
  root.append(ordinary, commerce);
  const original = mountCreatorSetup(ordinary, options);
  let panel, identity = '', proposal = null, paused = false, destroyed = false, retained = null;
  const isShopify = () => options.context().goal === 'shopify_product_finder';
  const notify = () => options.onChange();
  function update() {
    if (paused || destroyed) return;
    ordinary.hidden = isShopify(); commerce.hidden = !isShopify();
    if (!isShopify()) { panel?.destroy(); panel = null; identity = ''; proposal = null; retained = null; original.update(); return; }
    const { owner } = options.context();
    if (owner !== options.shopifyOwner) {
      panel?.destroy(); panel = null; identity = ''; proposal = null; retained = null;
      commerce.textContent = 'Choose the connected Shopify workspace to create this guide.'; return;
    }
    commerce.inert = options.locked();
    if (options.recovered()) { panel?.destroy(); panel = null; identity = ''; commerce.textContent = 'Your reviewed guide is retained. Recover draft to check the original creation attempt.'; return; }
    if (identity === owner && panel) return;
    panel?.destroy(); proposal = null; identity = owner;
    panel = mountShopifyFinder(commerce, {
      creation: true, supportsV3:options.shopifyConfigVersions?.includes(3)===true, supportsV2:options.shopifyConfigVersions?.includes(2)===true,
      connectionGuid: options.connectionGuid, restored: retained?.owner === owner ? retained.draft : null,
      isCurrent: () => !destroyed && !paused && isShopify() && options.context().owner === owner,
      isBusy: options.locked,
      request: async (path, body) => {
        const response = await guardedFetch(`${AuthController.getConfig().BASE_URL}/m5t/v5/shopify${path}`, {
          method: body === undefined ? 'GET' : 'POST', headers: {'Content-Type':'application/json'},
          ...(body === undefined ? {} : {body:JSON.stringify(body)}),
        });
        const data = await response.json();
        if (!response.ok) throw Object.assign(new Error(data.message || data.error), {code:data.error});
        return data;
      },
      onSettled: () => { notify(); if (proposal) options.onPreviewReady?.(); },
      onDirty: () => { proposal = null; options.onInvalidate?.(); notify(); },
      onPreview: steps => { if (!steps) { proposal = null; notify(); } },
      onApply: (binding, steps, definition) => { proposal = {binding, steps, definition}; notify(); options.onReviewed?.(); },
    });
    retained = null;
  }
  return {
    update,
    validate: () => !isShopify() ? original.validate() : options.recovered() ? ''
      : options.context().owner !== options.shopifyOwner ? 'Choose the connected Shopify workspace.'
      : panel?.busy() ? 'Wait for the catalog check to finish.'
      : !proposal ? 'Choose products to prepare your guide.' : '',
    changes: () => proposal ? {
      ...Object.fromEntries(Object.entries(proposal.definition).filter(([key]) => key !== 'mag_macro_steps')
        .map(([key,value]) => [key === 'mag_id' ? key : key.replace(/^mag_/, ''), structuredClone(value)])),
      product_finder: structuredClone(proposal.binding), flows: {chat:structuredClone(proposal.steps)},
    } : {},
    snapshot: () => original.snapshot(),
    focus: () => isShopify() ? commerce.querySelector('[data-generate],select')?.focus() : original.focus(),
    suspend() {
      if (panel && isShopify()) retained = {owner:options.context().owner,draft:panel.snapshot()};
      paused = true; original.suspend(); panel?.destroy(); panel = null; identity = ''; proposal = null;
      options.onInvalidate?.();
    },
    resume() { paused = false; original.resume(); update(); },
    destroy() { destroyed = true; original.destroy(); panel?.destroy(); },
  };
}
